import { SelectionModel } from '@angular/cdk/collections';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IDatatableColumn } from './datatable-column.interface';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PdfPreviewComponent } from '../pdf-preview/pdf-preview.component';
import { VimeoPreviewComponent } from '../vimeo-preview/vimeo-preview.component';
import { JsonPreviewComponent } from '../json-preview/json-preview.component';
import { paginationPosition } from '../configs/global-values.constants';
import { ViewportScroller } from '@angular/common';
import { ImagePreviewComponent } from '../image-preview/image-preview.component';

@Component({
  selector: 'app-datatable',
  templateUrl: './datatable.component.html',
  styleUrls: ['./datatable.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatatableComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSort, { static: true }) private _sort: MatSort;
  // @ViewChildren(MatRow, { read: ElementRef }) private _rows!: QueryList<
  //   ElementRef<HTMLTableRowElement>
  // >;
  private _unsubscribe$ = new Subject<void>();
  private _columns: IDatatableColumn[] = [];
  private _pageIndex: number;
  private _pageSize: number = 50;
  ctrlPressed = false;

  @ViewChild('paginator', { static: true }) paginator: MatPaginator;

  @Input()
  name = '';

  @Input()
  public set data(d: any[]) {
    this.dataSource.data = d;
    this.selection.clear();
  }

  @Input()
  public set columns(columns: IDatatableColumn[]) {
    if (columns) {
      this._columns = columns;
      console.log('columns: ', this._columns);
      this._setInternalColumns();
    }
  }

  @Input()
  public set pageIndex(index: number) {
    this._pageIndex = index;
    setTimeout(() => {
      this.paginator._changePageSize(this.paginator.pageSize);
    }, 300);
  }

  @Input()
  public set pageSize(pageSize: number) {
    this._pageSize = pageSize;
    setTimeout(() => {
      this.paginator._changePageSize(this.paginator.pageSize);
    }, 300);
  }

  public get columns(): IDatatableColumn[] {
    return this._columns;
  }

  public get pageIndex(): number {
    return this._pageIndex;
  }

  public get pageSize(): number {
    return this._pageSize ? this._pageSize : 20;
  }

  internalColumns: IDatatableColumn[] = [];

  @Input()
  cordX: number;

  @Input()
  cordY: number;

  @Input()
  selectedId: string;

  @Input()
  totalCount = 0;

  @Input()
  filterable = true;

  @Input()
  hideRefreshBtn = false;

  @Input()
  loading = false;

  @Input()
  showIndexColumn = false;

  @Input()
  selectable = false;

  length: number;

  displayedColumns: string[];

  @Input()
  tableTools: TemplateRef<any>;

  @Input()
  paginationPosition: string = paginationPosition.both;

  @Input()
  searchParam: string = '';

  @Output()
  editItemEvent = new EventEmitter();

  @Output()
  cloneItemEvent = new EventEmitter();

  @Output()
  deleteItemEvent = new EventEmitter();

  @Output()
  refreshEvent = new EventEmitter();

  @Output()
  pageChangingEvent = new EventEmitter<PageEvent>();

  @Output()
  searchParamEvent = new EventEmitter<string>();

  dataSource = new MatTableDataSource<any>([]);
  selection = new SelectionModel<any>(true, []);

  filterCtrl = new FormControl<string>('');

  private _setInternalColumns(): void {
    this.displayedColumns = this._columns.map(
      (column: IDatatableColumn) => column.key,
    );
    if (this.showIndexColumn) {
      this.displayedColumns.unshift('#');
    }
    if (this.selectable) {
      this.displayedColumns.unshift('select');
    }
  }

  constructor(
    private _modalService: NgbModal,
    private _scroller: ViewportScroller,
  ) {}

  ngOnInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this._sort;
  }

  ngAfterViewInit(): void {
    this.filterCtrl.valueChanges
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((searchTerm: string) => {
        this.searchParamEvent.emit(searchTerm);
        this.dataSource.filter = searchTerm.trim().toLowerCase();
      });

    if (this.searchParam) {
      this.filterCtrl.setValue(this.searchParam);
    }
    // logic for scrolling
    setTimeout(() => {
      this._scroller.scrollToPosition([this.cordX, this.cordY]);
    }, 300);
  }

  clearSearchField(): void {
    this.filterCtrl.setValue('');
  }

  pageChangeEvent(event: PageEvent): void {
    this.paginator.pageIndex = event.pageIndex;
    this.paginator.pageSize = event.pageSize;
    this.pageChangingEvent.emit(event);
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  toggleAllRows(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.data);
  }

  checkboxLabel(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${
      row.position + 1
    }`;
  }

  drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.columns, event.previousIndex, event.currentIndex);
  }

  openPreviewPDFModal(url: string): void {
    const modalRef = this._modalService.open(PdfPreviewComponent, {
      size: 'lg',
    });
    modalRef.componentInstance.url = url;
  }

  openPreviewJSONModal(json: object): void {
    const modalRef = this._modalService.open(JsonPreviewComponent, {
      size: 'lg',
    });
    modalRef.componentInstance.json = json;
  }

  openPreviewVimeoModal(url: string): void {
    const modalRef = this._modalService.open(VimeoPreviewComponent, {
      size: 'lg',
    });
    modalRef.componentInstance.url = url;
  }

  openPreviewImageModel(url: string): void {
    const modalRef = this._modalService.open(ImagePreviewComponent, {
      size: 'md',
    });
    modalRef.componentInstance.url = url;
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  editItemClicked(item: any): void {
    this.editItemEvent.emit(item);
  }

  cloneItemClicked(item: any): void {
    this.cloneItemEvent.emit(item);
  }

  deleteItemClicked(item: any): void {
    this.deleteItemEvent.emit(item);
  }

  refresh(): void {
    this.refreshEvent.emit();
  }
}
